import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Drives the sidebar logo's one-cycle-per-trigger animation pattern.
 *
 * The orb plays one full cycle on mount (the intro) and then parks. Each
 * call to triggerCycle() plays exactly one fresh cycle. Re-triggering
 * mid-cycle restarts the timer so the user always gets a full periodMs of
 * animation from the most recent trigger; holding hover without re-entering
 * does NOT loop.
 */
export function useOrbAnimationCycle(periodMs: number): {
  playing: boolean
  triggerCycle: () => void
} {
  const [playing, setPlaying] = useState(true)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleStop = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      setPlaying(false)
    }, periodMs)
  }, [periodMs])

  const triggerCycle = useCallback(() => {
    setPlaying(true)
    scheduleStop()
  }, [scheduleStop])

  useEffect(() => {
    scheduleStop()
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [scheduleStop])

  return { playing, triggerCycle }
}
